import { useEffect, useRef, useState, useCallback } from "react";
import api from "../lib/api";
import {
  Bell,
  BellRing,
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle2,
  XCircle,
  Clock,
  Wallet,
  Sparkles,
  RefreshCw,
  TrendingUp,
} from "lucide-react";

/* ---------- formatting helpers ---------- */
const fmt = (n) => `৳${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const timeAgo = (iso) => {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!t) return "—";
  const diff = Math.floor((Date.now() - t) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

/* ---------- per-type visual config ---------- */
const TYPE_META = {
  deposit_sent: {
    Icon: ArrowDownToLine,
    accent: "emerald",
    iconBg: "linear-gradient(135deg,#34d399,#059669)",
    iconColor: "#06140b",
  },
  withdraw_pending: {
    Icon: Clock,
    accent: "amber",
    iconBg: "linear-gradient(135deg,#fbbf24,#f59e0b)",
    iconColor: "#1a1208",
  },
  withdraw_approved: {
    Icon: CheckCircle2,
    accent: "emerald",
    iconBg: "linear-gradient(135deg,#34d399,#10b981)",
    iconColor: "#06140b",
  },
  withdraw_rejected: {
    Icon: XCircle,
    accent: "rose",
    iconBg: "linear-gradient(135deg,#fb7185,#f43f5e)",
    iconColor: "#1a0509",
  },
};

const ACCENT = {
  emerald: { text: "text-emerald-300", chip: "bg-emerald-500/15 border-emerald-500/30 text-emerald-300" },
  amber:   { text: "text-amber-300",   chip: "bg-amber-500/15 border-amber-500/30 text-amber-300" },
  rose:    { text: "text-rose-300",    chip: "bg-rose-500/15 border-rose-500/30 text-rose-300" },
};

/* ============================================================ */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [commission30, setCommission30] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agent/notifications", { params: { limit: 30 } });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
      setCommission30(data.commission_30d || 0);
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + poll every 30s
  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleToggle = async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen && unread > 0) {
      // Optimistically mark all as read
      setUnread(0);
      setItems((arr) => arr.map((it) => ({ ...it, is_unread: false })));
      try {
        await api.post("/agent/notifications/mark-read");
      } catch {
        /* ignore */
      }
    }
  };

  const Bell0 = unread > 0 ? BellRing : Bell;

  return (
    <div ref={wrapRef} className="relative" data-testid="agent-notification-wrap">
      <button
        type="button"
        onClick={handleToggle}
        data-testid="agent-notification-bell"
        aria-label="Notifications"
        className="relative h-10 w-10 rounded-xl grid place-items-center bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] hover:border-emerald-500/30 transition group"
      >
        <Bell0
          size={16}
          className={`${unread > 0 ? "text-emerald-300" : "text-white/70 group-hover:text-white"} transition`}
          style={unread > 0 ? { animation: "agent-bell-shake 1.6s ease-in-out infinite" } : undefined}
        />
        {unread > 0 && (
          <span
            data-testid="agent-notification-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full grid place-items-center text-[10px] font-black"
            style={{
              background: "linear-gradient(135deg,#f43f5e,#e11d48)",
              color: "#fff",
              boxShadow: "0 0 0 2px #070d18, 0 6px 14px -6px rgba(244,63,94,0.7)",
            }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="agent-notification-panel"
          className="fixed sm:absolute right-2 sm:right-0 left-2 sm:left-auto top-14 sm:top-12 sm:w-[400px] max-w-[calc(100vw-1rem)] rounded-2xl border border-white/[0.08] overflow-hidden z-50"
          style={{
            background: "linear-gradient(180deg, rgba(12,18,30,0.98) 0%, rgba(8,12,22,0.99) 100%)",
            backdropFilter: "blur(20px)",
            boxShadow: "0 24px 60px -16px rgba(0,0,0,0.7), 0 0 0 1px rgba(16,185,129,0.06) inset",
            animation: "agent-pop-in 200ms cubic-bezier(0.34,1.56,0.64,1) both",
          }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between bg-gradient-to-r from-emerald-500/10 via-transparent to-transparent">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg grid place-items-center" style={{ background: "rgba(16,185,129,0.18)", border: "1px solid rgba(16,185,129,0.28)" }}>
                <Sparkles size={14} className="text-emerald-300" />
              </div>
              <div>
                <div className="text-[13px] font-extrabold tracking-tight">Activity</div>
                <div className="text-[10.5px] text-white/45">{items.length} recent event{items.length === 1 ? "" : "s"}</div>
              </div>
            </div>
            <button
              type="button"
              onClick={load}
              className="h-7 w-7 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/[0.08] transition"
              aria-label="Refresh"
              data-testid="agent-notification-refresh"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            </button>
          </div>

          {/* Commission stat strip */}
          <div className="px-4 py-2.5 border-b border-white/[0.06] flex items-center gap-3 bg-white/[0.015]">
            <div className="h-7 w-7 rounded-lg grid place-items-center" style={{ background: "rgba(245,158,11,0.18)", border: "1px solid rgba(245,158,11,0.32)" }}>
              <TrendingUp size={12} className="text-amber-300" />
            </div>
            <div className="flex-1">
              <div className="text-[9.5px] uppercase tracking-[1.5px] text-amber-300/85 font-bold">Commission earned · 30 days</div>
              <div className="font-mono font-extrabold text-[15px] text-amber-200" data-testid="agent-notification-commission-30d">
                {fmt(commission30)}
              </div>
            </div>
          </div>

          {/* List */}
          <div className="max-h-[420px] overflow-y-auto custom-scroll">
            {loading && items.length === 0 ? (
              <div className="py-10 grid place-items-center">
                <div className="h-7 w-7 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : items.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <div className="h-12 w-12 rounded-2xl bg-white/[0.04] mx-auto grid place-items-center mb-2.5 border border-white/[0.06]">
                  <Bell size={20} className="text-white/30" />
                </div>
                <div className="text-[12.5px] font-bold text-white/65">No activity yet</div>
                <div className="text-[10.5px] text-white/40 mt-0.5">Deposits, withdrawals & commissions will appear here.</div>
              </div>
            ) : (
              <ul className="divide-y divide-white/[0.04]" data-testid="agent-notification-list">
                {items.map((it, i) => (
                  <NotificationRow key={it.key || i} item={it} />
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-white/[0.06] text-[10.5px] text-white/40 text-center">
            Auto-refreshes every 30s · Last 30 days
          </div>
        </div>
      )}

      <style>{`
        @keyframes agent-bell-shake {
          0%,100% { transform: rotate(0); }
          15%     { transform: rotate(-12deg); }
          30%     { transform: rotate(10deg); }
          45%     { transform: rotate(-6deg); }
          60%     { transform: rotate(4deg); }
          75%     { transform: rotate(-2deg); }
        }
        @keyframes agent-pop-in {
          from { opacity: 0; transform: translateY(-6px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

/* ---------- single notification row ---------- */
function NotificationRow({ item }) {
  const meta = TYPE_META[item.type] || TYPE_META.deposit_sent;
  const accent = ACCENT[meta.accent] || ACCENT.emerald;
  const Icon = meta.Icon;

  return (
    <li
      data-testid={`agent-notification-item-${item.type}`}
      className={`px-4 py-3 flex items-start gap-3 transition hover:bg-white/[0.025] relative ${item.is_unread ? "bg-emerald-500/[0.025]" : ""}`}
    >
      {item.is_unread && (
        <span
          aria-hidden
          className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[26px] rounded-r-full"
          style={{ background: "linear-gradient(180deg,#34d399,#10b981)", boxShadow: "0 0 10px rgba(52,211,153,0.6)" }}
        />
      )}
      <div
        className="h-9 w-9 rounded-xl grid place-items-center flex-shrink-0"
        style={{ background: meta.iconBg, boxShadow: "0 8px 18px -10px rgba(0,0,0,0.4)" }}
      >
        <Icon size={15} strokeWidth={2.4} style={{ color: meta.iconColor }} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <div className="text-[12.5px] font-bold text-white truncate">{item.title}</div>
          {item.is_unread && (
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
          )}
        </div>
        <div className="text-[11.5px] text-white/60 mt-0.5 truncate">{item.description}</div>
        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          {item.commission > 0 && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-bold border ${accent.chip}`}>
              <Wallet size={9} /> +{fmt(item.commission)} commission
            </span>
          )}
          <span className="text-[10px] text-white/35">{timeAgo(item.at)}</span>
        </div>
      </div>
    </li>
  );
}
